import { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/lib/supabase';
import { PainPointsAnalysis } from '@/components/tools/PainPointsAnalysis';
import { AIToolRecommendations } from '@/components/tools/AIToolRecommendations';
import { ImplementationTimeline } from '@/components/tools/ImplementationTimeline';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { Layout } from '@/components/ui/Layout';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

export function Tools() {
  const { organizationId, organizationName } = useOrganization();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [surveyData, setSurveyData] = useState<any[]>([]);
  const [selectedPainPoints, setSelectedPainPoints] = useState<string[]>([]);
  const [recommendedTools, setRecommendedTools] = useState<any[]>([]);

  useEffect(() => {
    if (organizationId) {
      loadSurveyData();
    } else {
      // Clear data if no organization is selected
      setSurveyData([]);
      setSelectedPainPoints([]);
      setRecommendedTools([]);
      setLoading(false);
    }
  }, [organizationId]);

  const loadSurveyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all survey responses with their questions
      const { data: responses, error: responsesError } = await supabase
        .from('survey_responses')
        .select(`
          *,
          survey_questions!inner (
            text,
            type
          ),
          survey_respondents!inner (
            department_id,
            team_id
          )
        `)
        .eq('organization_id', organizationId);

      if (responsesError) {
        throw new Error(`Failed to load survey data: ${responsesError.message}`, { cause: responsesError });
      }

      // Filter out any responses without valid question data
      const validResponses = responses?.filter(response => 
        response.survey_questions && 
        response.survey_questions.text &&
        response.answer &&
        response.answer.trim() !== ''
      ) || [];

      // Transform responses to match expected format
      const transformedResponses = validResponses.map(response => ({
        question: response.survey_questions,
        answer: response.answer,
        respondent: response.survey_respondents
      }));

      setSurveyData(transformedResponses);

    } catch (err) {
      console.error('Error loading survey data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load survey data'));
      setSurveyData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePainPointsSelected = (painPoints: string[]) => {
    setSelectedPainPoints(painPoints);
    analyzeAndRecommendTools(painPoints);
  };

  const analyzeAndRecommendTools = (painPoints: string[]) => {
    const recommendations = painPoints.map(point => ({
      painPoint: point,
      tools: []
    }));
    setRecommendedTools(recommendations);
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (!organizationId) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              Please select an organization to view available tools.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
            {organizationName ? `${organizationName} Tools` : 'Tools'}
          </h1>
          <Button variant="outline">
            <FileText className="h-4 w-4 mr-2" />
            Generate Report
          </Button>
        </div>

        {error && (
          <ErrorDisplay 
            error={error}
            context="Tools"
            onRetry={loadSurveyData}
          />
        )}

        {/* Pain Points Analysis */}
        <PainPointsAnalysis
          surveyData={surveyData}
          onPainPointsSelected={handlePainPointsSelected}
        />

        {/* Implementation Timeline */}
        <ImplementationTimeline
          painPoints={selectedPainPoints}
          recommendedTools={recommendedTools}
        />
      </div>
    </Layout>
  );
}